import { CheckIcon, TagIcon, UserRoundSearchIcon, UsersIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { OptionTiles } from '@/components/OptionTiles';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useContacts, useTags } from '@/features/contacts/queries';
import { useDebounce } from '@/lib/hooks';
import type { ContactDto } from '@/lib/types';
import { cn } from '@/lib/utils';

export type AudienceMode = 'all' | 'tags' | 'selected';

/**
 * Who a message goes to: one segmented choice (everyone, by tag, hand-picked people) with the detail for the
 * chosen one underneath, only when there is something to choose. Tags come from the Contacts page; people are
 * found by name or number.
 */
export function AudiencePicker({
  jobId,
  mode,
  onModeChange,
  tags,
  onTagsChange,
  selected,
  onSelectedChange,
  totalContacts,
}: {
  jobId: string;
  mode: AudienceMode;
  onModeChange: (m: AudienceMode) => void;
  tags: string[];
  onTagsChange: (t: string[]) => void;
  selected: Map<string, ContactDto>;
  onSelectedChange: (s: Map<string, ContactDto>) => void;
  totalContacts: number | undefined;
}) {
  const allTags = useTags(jobId);
  const tagItems = allTags.data?.items ?? [];

  return (
    <div className="grid gap-3">
      <OptionTiles
        ariaLabel="Send to"
        value={mode}
        onChange={onModeChange}
        options={[
          { value: 'all', icon: UsersIcon, title: 'Everyone', hint: totalContacts !== undefined ? `${totalContacts.toLocaleString()} contacts, minus opt-outs` : 'All subscribed contacts' },
          { value: 'tags', icon: TagIcon, title: 'By tag', hint: tags.length ? `${tags.length} ${tags.length === 1 ? 'tag' : 'tags'} chosen` : 'Trades, shifts, companies' },
          { value: 'selected', icon: UserRoundSearchIcon, title: 'Pick people', hint: selected.size ? `${selected.size} selected` : 'Search by name or number' },
        ]}
      />

      {mode === 'tags' && (
        <div className="grid gap-2">
          <div className="flex flex-wrap gap-1.5">
            {tagItems.length === 0 && <span className="text-muted-foreground text-xs">{allTags.isPending ? 'Loading tags…' : 'No tags yet. Add tags to contacts first.'}</span>}
            {tagItems.map((t) => {
              const on = tags.includes(t.tag);
              return (
                <button
                  key={t.tag}
                  type="button"
                  aria-pressed={on}
                  onClick={() => onTagsChange(on ? tags.filter((x) => x !== t.tag) : [...tags, t.tag])}
                  className={cn('flex h-8 items-center gap-1.5 border px-2.5 text-xs transition-colors', on ? 'border-primary bg-primary text-primary-foreground' : 'bg-card hover:border-foreground/30')}
                >
                  {on && <CheckIcon className="size-3" aria-hidden />}
                  {t.tag}
                  <span className={cn('tabular-nums', on ? 'opacity-80' : 'text-muted-foreground')}>{t.count}</span>
                </button>
              );
            })}
          </div>
          <p className="text-muted-foreground text-xs">Anyone with at least one of the chosen tags. Manage tags on the Contacts page.</p>
        </div>
      )}

      {mode === 'selected' && <ContactPicker jobId={jobId} selected={selected} onChange={onSelectedChange} />}
    </div>
  );
}

function ContactPicker({ jobId, selected, onChange }: { jobId: string; selected: Map<string, ContactDto>; onChange: (s: Map<string, ContactDto>) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const debounced = useDebounce(q, 250);
  const results = useContacts(jobId, { q: debounced || undefined, page: 1, pageSize: 20, sort: 'name', dir: 'asc' });

  const toggle = (c: ContactDto) => {
    const next = new Map(selected);
    if (next.has(c.id)) next.delete(c.id);
    else next.set(c.id, c);
    onChange(next);
  };

  return (
    <div className="grid gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="bg-card w-full justify-start font-normal" role="combobox" aria-expanded={open}>
            <UserRoundSearchIcon className="text-muted-foreground" />
            {selected.size === 0 ? <span className="text-muted-foreground">Search contacts by name, phone or company…</span> : `${selected.size} selected · add more`}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Name, phone, company…" value={q} onValueChange={setQ} />
            <CommandList>
              {results.isPending ? (
                <div className="text-muted-foreground p-3 text-xs">Searching…</div>
              ) : (
                <>
                  <CommandEmpty>No contacts found.</CommandEmpty>
                  <CommandGroup>
                    {(results.data?.items ?? []).map((c) => {
                      const on = selected.has(c.id);
                      return (
                        <CommandItem key={c.id} value={c.id} onSelect={() => toggle(c)} disabled={c.optedOut}>
                          <span className={cn('flex size-4 items-center justify-center border', on && 'bg-primary border-primary text-primary-foreground')}>{on && <CheckIcon className="size-3" />}</span>
                          <span className="flex-1 truncate">{c.fullName}</span>
                          <span className="text-muted-foreground text-xs tabular-nums">{c.phoneDisplay}</span>
                          {c.optedOut && <Badge variant="warning">Opted out</Badge>}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {[...selected.values()].map((c) => (
            <Badge key={c.id} variant="secondary" className="h-7 gap-1 pr-1 pl-2">
              {c.fullName}
              <button type="button" aria-label={`Remove ${c.fullName}`} onClick={() => toggle(c)} className="hover:bg-foreground/10 grid size-5 place-items-center">
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
          <Button type="button" variant="ghost" size="xs" onClick={() => onChange(new Map())}>
            Clear
          </Button>
        </div>
      )}
    </div>
  );
}
